import cx from 'classnames';
import Helmet from 'react-helmet';
import type { ReactElement, VoidFunctionComponent } from 'react';

import Locator from '../../../shared/Locator';
import { useParams } from '../../contexts/RouterContext';
import useGetScope from '../../hooks/useGetScope';
import { AccessGate } from '../generic';
import FallbackImage from '../generic/FallbackImage';
import { useProject } from '../projects/contexts/ProjectContext';
import { issueOverviewFeature } from '../settings/billing/features/consts';

import { orgHasIssueOverviewEnabled } from './accessControl';
import BreakdownSection from './breakdowns/BreakdownSection';
import FilterSection from './filters/FilterSection';
import useIssueOverview from './hooks/useIssueOverview';
import useIssueOverviewData from './hooks/useIssueOverviewData';
import IssueHistoryGraph from './IssueHistoryGraph';
import KPISection from './KPISection';
import NoProjectsContent from './NoProjectsContent';

import styles from './IssueOverview.module.less';

// TODO: Merge this with app/components/settings/errors
const NetworkErrorContent: VoidFunctionComponent = () => (
  <FallbackImage src="error-plug" alt="Network error" hasPadding>
    <div>Oops! There was an error.</div>
    <div>Please try again in a bit.</div>
  </FallbackImage>
);

const NotEnabledContent: VoidFunctionComponent = () => (
  <FallbackImage src="error-plug" alt="Network error" hasPadding>
    <div>No Issue Overview Data!</div>
    <div>Snapshots are taken nightly. Please check back tomorrow.</div>
  </FallbackImage>
);

const NonDefaultBranchRevisionContent: VoidFunctionComponent<{ latestRevision: string; defaultBranch: string }> = ({
  latestRevision,
  defaultBranch,
}) => (
  <FallbackImage src="no-results" alt="No results" hasPadding>
    <div className={styles.nonDefaultBranchRevision}>
      <div>Issue Overview is only available on the latest revision of the default branch.</div>
      <div className={styles.helperText}>
        <div>To access this page, switch to</div>
        <div>
          Branch: <span className={styles.revisionBranch}>{defaultBranch}</span>
        </div>
        <div>
          Revision: <span className={styles.revisionBranch}>{latestRevision}</span>
        </div>
      </div>
    </div>
  </FallbackImage>
);

// TODO: Merge this with app/components/settings/errors
const InvalidPermissionContent: VoidFunctionComponent = () => (
  <FallbackImage src="no-results" alt="No results" hasPadding>
    <div>You do not have permission to access this page. Please contact your FOSSA administrator to get access.</div>
  </FallbackImage>
);

const IssueOverviewContent: VoidFunctionComponent = () => {
  const { filters } = useIssueOverview();
  const { value } = useIssueOverviewData();
  const project = useProject();

  // eslint-disable-next-line @typescript-eslint/naming-convention -- this is the name of the param
  const { revision_id, reference } = useParams<{ revision_id: string; reference: string }>();

  // If we have a revision_id and reference, we know we are on a project scoped page, and not the global one.
  const scope = useGetScope();
  const isProjectScoped = scope === 'project';

  const lastAnalyzedRevision = new Locator(project.data?.last_analyzed_revision ?? '');

  const isDefaultBranch = reference === project.data?.default_branch;
  const isLatestRevision = revision_id === lastAnalyzedRevision.revision.value;

  let content: ReactElement | null = null;
  if (value.errors) {
    // TODO: The asyncValue errors should really be changed to include more information like the status code or error
    // name so that we don't have to rely on message matching.
    if (value.errors[0]?.includes('missing permission')) {
      content = <InvalidPermissionContent />;
    } else if (value.errors[0]?.includes('not ready yet')) {
      content = <NotEnabledContent />;
    } else {
      content = <NetworkErrorContent />;
    }
  } else if (value.value?.totalProjects === 0) {
    content = <NoProjectsContent />;
  } else if (isProjectScoped && !(isDefaultBranch && isLatestRevision)) {
    content = (
      <NonDefaultBranchRevisionContent
        latestRevision={lastAnalyzedRevision.revision.value ?? ''}
        defaultBranch={project.data?.default_branch ?? ''}
      />
    );
  }

  return (
    <>
      <Helmet title="Issue Overview" />
      <div className={cx('card', styles.container)}>
        <div className={cx(styles.section, styles.header)}>
          <div className={styles.title}>Issue Overview</div>
          <FilterSection isDisabled={!!content} />
        </div>
        {content ?? (
          <>
            <KPISection items={value.value?.counts ?? []} isLoading={value.isLoading || !value.value} />
            <IssueHistoryGraph
              items={value.value?.counts ?? []}
              isLoading={value.isLoading || !value.value}
              start={filters.start}
              end={filters.end}
            />
            <BreakdownSection data={value} category={filters.category} />
          </>
        )}
      </div>
    </>
  );
};

/**
 * The main component for displaying the Issue Overview.
 */
const IssueOverview: VoidFunctionComponent = () => {
  return (
    <AccessGate behavior="showPage" verify={() => orgHasIssueOverviewEnabled()} focusFeature={issueOverviewFeature}>
      <IssueOverviewContent />
    </AccessGate>
  );
};

export default IssueOverview;
